/**
 * API Client for fetching practices from remote server
 * Base URL: https://api-pr788.myoact.io/api/
 */

import type { Practice, PracticeLocationResponse } from "@/data/practices";

// ===================================================
// CONFIGURATION
// ===================================================

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.myoact.de/api";
const API_TIMEOUT = 10000; // 10 seconds

// ===================================================
// ERROR TYPES
// ===================================================

export class ApiError extends Error {
    constructor(
        message: string,
        public statusCode?: number,
        public code?: string
    ) {
        super(message);
        this.name = "ApiError";
    }
}

export class NetworkError extends ApiError {
    constructor(message: string = "Network error. Please check your connection.") {
        super(message, undefined, "NETWORK_ERROR");
        this.name = "NetworkError";
    }
}

export class TimeoutError extends ApiError {
    constructor(message: string = "Request timed out. Please try again.") {
        super(message, undefined, "TIMEOUT_ERROR");
        this.name = "TimeoutError";
    }
}

export class NotFoundError extends ApiError {
    constructor(message: string = "Resource not found.") {
        super(message, 404, "NOT_FOUND");
        this.name = "NotFoundError";
    }
}

export class ServerError extends ApiError {
    constructor(message: string = "Server error. Please try again later.") {
        super(message, 500, "SERVER_ERROR");
        this.name = "ServerError";
    }
}

// ===================================================
// API RESPONSE TYPES
// ===================================================

export interface ApiResponse<T> {
    data: T;
    success: boolean;
    message?: string;
}

export interface PracticeAddress {
    zip?: string;
    city?: string;
    state?: string;
    street?: string;
    comment?: string;
    country?: string;
    building?: string;
    latitude?: number;
    apartment?: string;
    longitude?: number;
}

// ===================================================
// PRACTICE SERVICE TYPES
// ===================================================

export interface Translation {
    lang: string;
    value: string;
}

export interface PracticeServiceDto {
    id: string;
    name: Translation[];
    typeId: string;
    type: PracticeServiceTypeDto;
}

export interface PracticeServiceTypeDto {
    id: string;
    name: Translation[];
    code: string;
    practiceServices?: PracticeServiceDto[];
}

export interface PracticeInfo {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    specialization?: string;
    createdAt?: string;
    updatedAt?: string;
    isActive?: boolean;
    services?: PracticeServiceDto[];
    isPublic?: boolean;
    stripeId?: string;
    users?: unknown[];
    locations?: unknown[];
    isWizardCompleted?: boolean;
    logoUrl?: string;
    context?: string;
}

export interface PracticeApiResponse {
    id: string;
    address: PracticeAddress;
    phone?: string;
    email?: string;
    siteUrl?: string;
    practiceId: string;
    createdAt?: string;
    updatedAt?: string;
    isActive?: boolean;
    practice: PracticeInfo;
}

// ===================================================
// HELPER FUNCTIONS
// ===================================================

/**
 * Creates an AbortController with timeout
 */
function createTimeoutController(timeout: number): { controller: AbortController; timeoutId: NodeJS.Timeout } {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    return { controller, timeoutId };
}

/**
 * Converts a raw API location response into a PracticeLocationResponse
 */
function toLocationResponse(data: PracticeApiResponse): PracticeLocationResponse {
    return {
        id: data.id,
        address: data.address || {},
        phone: data.phone,
        email: data.email,
        siteUrl: data.siteUrl,
        practiceId: data.practiceId,
        isActive: data.isActive,
    };
}

/**
 * Groups an array of API location responses into Practice objects
 */
function locationsToPractices(locations: PracticeApiResponse[]): Practice[] {
    return locations.map((loc) => {
        const info = loc.practice;
        return {
            id: loc.id,
            practiceId: info.id,
            name: info.name || "Unknown Practice",
            phone: info.phone,
            email: info.email,
            specialization: info.specialization,
            context: info.context,
            logoUrl: info.logoUrl,
            isWizardCompleted: info.isWizardCompleted ?? false,
            isPublic: info.isPublic ?? false,
            services: (info.services || []).map((s) => ({
                id: s.id,
                name: s.name || [],
                typeId: s.typeId || "",
                type: s.type || { id: "", name: [], code: "" },
            })),
            users: undefined,
            locations: [toLocationResponse(loc)],
        };
    });
}

/**
 * Handles API response and throws appropriate errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const errorBody = await response.text().catch(() => "");
        console.error(`[handleResponse] ❌ HTTP ${response.status}: ${errorBody.substring(0, 300)}`);

        switch (response.status) {
            case 400:
                throw new ApiError(`Bad request: ${errorBody || "Invalid request"}`, 400, "BAD_REQUEST");
            case 401:
                throw new ApiError("Unauthorized. Please log in.", 401, "UNAUTHORIZED");
            case 403:
                throw new ApiError("Access forbidden.", 403, "FORBIDDEN");
            case 404:
                throw new NotFoundError();
            case 429:
                throw new ApiError("Too many requests. Please wait and try again.", 429, "RATE_LIMITED");
            case 500:
            case 502:
            case 503:
            case 504:
                throw new ServerError(`Server error (${response.status}): ${errorBody || "Please try again later"}`);
            default:
                throw new ApiError(`Request failed: ${errorBody || response.statusText}`, response.status);
        }
    }

    try {
        return await response.json();
    } catch {
        throw new ApiError("Failed to parse server response", undefined, "PARSE_ERROR");
    }
}

// ===================================================
// API CLIENT
// ===================================================

export const apiClient = {
    /**
     * Fetches all active practices from the API
     * @returns Promise<Practice[]> - Array of practices
     * @throws ApiError, NetworkError, TimeoutError, ServerError
     */
    async getActivePractices(): Promise<Practice[]> {
        const url = `${API_BASE_URL}/v1/locations/active`;
        console.debug("[apiClient.getActivePractices] 🔄 Fetching:", url);

        const { controller, timeoutId } = createTimeoutController(API_TIMEOUT);

        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                signal: controller.signal,
                cache: "no-store", // Ensure fresh data for SSR
            });

            clearTimeout(timeoutId);

            console.debug("[apiClient.getActivePractices] Response status:", response.status);

            const rawData = await handleResponse<PracticeApiResponse[] | ApiResponse<PracticeApiResponse[]>>(response);

            console.debug("[apiClient.getActivePractices] Raw response type:",
                typeof rawData, Array.isArray(rawData) ? `array[${rawData.length}]` : Object.keys(rawData as object));

            // Handle both array response and wrapped response
            const practices = Array.isArray(rawData) ? rawData : (rawData as ApiResponse<PracticeApiResponse[]>).data;

            if (!Array.isArray(practices)) {
                console.error("[apiClient.getActivePractices] ❌ Invalid format. Raw data:",
                    JSON.stringify(rawData).substring(0, 500));
                throw new ApiError("Invalid response format: expected array of practices", undefined, "INVALID_FORMAT");
            }

            console.debug(`[apiClient.getActivePractices] Grouping ${practices.length} locations into practices`);

            const transformed = locationsToPractices(practices);

            console.debug(`[apiClient.getActivePractices] ✅ Successfully loaded ${transformed.length} practices`);
            return transformed;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof ApiError) {
                console.error(`[apiClient.getActivePractices] ❌ API Error [${error.code}]: ${error.message} (status: ${error.statusCode})`);
                throw error;
            }

            if (error instanceof Error) {
                console.error(`[apiClient.getActivePractices] ❌ ${error.name}: ${error.message}`);
                if (error.name === "AbortError") {
                    throw new TimeoutError();
                }
                if (error.message.includes("fetch") || error.message.includes("network")) {
                    throw new NetworkError();
                }
            }

            console.error("[apiClient.getActivePractices] ❌ Unknown error:", error);
            throw new NetworkError("An unexpected error occurred");
        }
    },

    /**
     * Fetches all practice service types (with nested services) from the API
     * @returns Promise<PracticeServiceTypeDto[]> - Array of practice service types
     */
    async getPracticeServices(): Promise<PracticeServiceTypeDto[]> {
        const url = `${API_BASE_URL}/v1/practice-services`;
        console.debug("[apiClient.getPracticeServices] 🔄 Fetching:", url);

        const { controller, timeoutId } = createTimeoutController(API_TIMEOUT);

        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                signal: controller.signal,
                cache: "no-store",
            });

            clearTimeout(timeoutId);

            const rawData = await handleResponse<PracticeServiceTypeDto[] | ApiResponse<PracticeServiceTypeDto[]>>(response);

            const serviceTypes = Array.isArray(rawData) ? rawData : (rawData as ApiResponse<PracticeServiceTypeDto[]>).data;

            if (!Array.isArray(serviceTypes)) {
                throw new ApiError("Invalid response format: expected array of practice service types", undefined, "INVALID_FORMAT");
            }

            console.debug(`[apiClient.getPracticeServices] ✅ Successfully loaded ${serviceTypes.length} service types`);
            return serviceTypes;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof ApiError) throw error;

            if (error instanceof Error) {
                if (error.name === "AbortError") throw new TimeoutError();
                if (error.message.includes("fetch") || error.message.includes("network")) throw new NetworkError();
            }

            throw new NetworkError("An unexpected error occurred");
        }
    },

    /**
     * Fetches a single practice by ID (looks up from all active practices)
     */
    async getPracticeById(id: string): Promise<Practice | undefined> {
        const practices = await this.getActivePractices();
        return practices.find((p) => p.id === id);
    },
};

// ===================================================
// REACT HOOKS HELPERS
// ===================================================

export type FetchState<T> =
    | { status: "idle"; data: null; error: null }
    | { status: "loading"; data: null; error: null }
    | { status: "success"; data: T; error: null }
    | { status: "error"; data: null; error: ApiError };

/**
 * Initial fetch state
 */
export const initialFetchState: FetchState<never> = {
    status: "idle",
    data: null,
    error: null,
};

export default apiClient;
